import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { Row, Col, Button as BButton } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSave,
  faPlusCircle,
  faPollH,
  faPaperclip,
  faPhotoVideo,
  faFileAlt,
  faSortAlt,
  faUndo,
  faCogs,
} from '@fortawesome/pro-solid-svg-icons'
import * as fromBlockActions from 'store/actions/blocks.action'
import * as fromLessonActions from 'store/actions/lessons-admin.action'
import { IMainState } from 'store/states'
import { IBlockInstanceModel } from 'models/block.model'
import { BlockActionEnum } from 'enum/block-action.enum'
import { BlockTypes } from 'enum'
import Loader from 'components/ui/loader/Loader'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import VideoConference from 'assets/icons/VideoConference'
import SessionsBlockIcon from 'assets/icons/SessionsBlockIcon'
import * as fromCourseActions from 'store/actions/courses.action'
import useIntegrations from 'hooks/useIntegrations'
import Wrapper from 'components/reusables/wrapper'
import { useAccess } from 'contexts/AccessContext'
import { slices } from 'store/slices'
import { IQuestionBankData } from 'interfaces/questionBank'
import styles from './Index.module.scss'
import BlockType from './components/block-type/BlockType'
import { BlocksFormFooter } from './components/BlocksFormFooter'
import BlockTypeButton from './components/block-type-button/BlockTypeButton'
import BlockList from './components/blocks-list/BlockList'
import useActions from './useActions'
import ConfirmationDelete from './components/ConfirmationDelete'
import { EvaluationButton } from './components/EvaluationButton'
import {
  AiEvaluationModal,
  AiEvaluationBlockButton,
  AiEvaluationSuggestion,
  AiEvaluationToolbarButton,
} from './components/ai-evaluation'
import {
  MoveBlocksButton,
  CancelOrderButton,
  SaveOrderButton,
} from './components/ai-evaluation/BlocksToolbar.styled'

type Params = Record<'courseId' | 'lessonId', string>

const Index: React.FC = () => {
  const { t } = useTranslation()

  const { courseId, lessonId } = useParams<Params>()
  const dispatch = useDispatch()
  const history = useHistory()
  const { can, canAccessCourse, loading: accessLoading } = useAccess()

  useEffect(() => {
    if (
      !accessLoading &&
      (!can('manageInternalCourses') || !canAccessCourse(Number(courseId)))
    ) {
      window.location.href = '/forbidden'
    }
  }, [accessLoading, can, canAccessCourse, courseId])

  useEffect(() => {
    dispatch({ type: fromCourseActions.GET_ADMIN_COURSE, payload: courseId })
  }, [courseId, dispatch])

  const { integrationZoom } = useIntegrations()

  const { list, loading, aiEvaluationQuestionsEnabled, meetingsEnabled } =
    useSelector(({ blocks }: IMainState) => blocks)
  const editBlock = useSelector(({ blocks }: IMainState) => blocks).edit
  const [showOptions, setShowOptions] = useState(false)
  const [createOrEdit, setCreateOrEdit] = useState(BlockActionEnum.Create)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [showAiEvaluationModal, setShowAiEvaluationModal] = useState(false)
  const [highlightBlockId, setHighlightBlockId] = useState<number | null>(null)
  const [pendingEditBlockId, setPendingEditBlockId] = useState<number | null>(
    null
  )
  const [listAux, setListAux] = useState<IBlockInstanceModel[]>([])

  const {
    handleDelete,
    handleEdit,
    handleSelectedOptionChange,
    handleSubmit,
    handleSaveOrder,
    setOrder,
    setDraggable,
    setSelectedOption,
    setSelected,
    setLessonData,
    states: {
      loading: buttonLoading,
      draggable,
      selectedOption,
      selected,
      lessonData,
    },
  } = useActions({
    lessonId: +lessonId,
    onFinish: () => {
      setShowOptions(false)
    },
    onEdit: () => {
      setCreateOrEdit(BlockActionEnum.Edit)
    },
  })

  const hasEvaluation = useMemo(
    () =>
      list.some(
        (block) =>
          block.blockType === BlockTypes.BlockEvaluation ||
          block.blockType === BlockTypes.Evaluation
      ),
    [list]
  )

  const richTextCount = useMemo(
    () =>
      list.filter((block) => block.blockType === BlockTypes.BlockRichText)
        .length,
    [list]
  )

  const hasScorm = useMemo(
    () => list.some((block) => block.blockType === BlockTypes.BlockScorm),
    [list]
  )

  const hasSession = useMemo(
    () => list.some((block) => block.blockType === BlockTypes.BlockSession),
    [list]
  )

  useEffect(() => {
    setListAux(list.slice())
  }, [list])

  useLayoutEffect(() => {
    if (lessonId) {
      dispatch({ type: fromLessonActions.GET_LESSON, lessonId })
      dispatch({ type: fromBlockActions.GET_LESSON_BLOCKS_LIST, lessonId })
    }
    return () => {
      dispatch({ type: fromBlockActions.CLEAR_EDIT })
    }
  }, [dispatch, lessonId])

  const blockInstance = useMemo(
    () => list.find((block) => block.id === selected),
    [list, selected]
  )

  const onDeleteClick = (id: number) => {
    setShowDeleteConfirmationModal(true)
    setSelected(id)
  }

  const handleCancel = useCallback(() => {
    setSelectedOption(null)
    dispatch({ type: fromBlockActions.CLEAR_EDIT })
    setCreateOrEdit(BlockActionEnum.Create)
  }, [dispatch, setSelectedOption])

  const handleCancelDraggable = () => {
    setListAux(list.slice())
    setDraggable(false)
  }

  const handleSetDraggable = () => {
    setDraggable(true)
    setSelectedOption(null)
    setSelected(0)
    setCreateOrEdit(BlockActionEnum.Create)
    setShowOptions(false)
    dispatch({ type: fromBlockActions.CLEAR_EDIT })
  }

  const videoConferenceEnable = !!integrationZoom?.active

  const aiEvaluationFlagOn = aiEvaluationQuestionsEnabled
  const canUseAiEvaluation =
    aiEvaluationFlagOn && !hasEvaluation && richTextCount > 0 && !draggable

  const openAiEvaluationModal = useCallback(() => {
    if (!canUseAiEvaluation) return
    setShowAiEvaluationModal(true)
  }, [canUseAiEvaluation])

  // El modal de IA no persiste: deja el preview en el store (pendingPrefill) y
  // abre el editor del Banco de Preguntas v2 (evaluación nueva) ya rellenado,
  // para que la persona ajuste y guarde con el flujo manual habitual.
  const handleAiEvaluationConfirm = useCallback(
    (data: IQuestionBankData) => {
      dispatch(slices.questionBank.setPrefill(data))
      dispatch(
        fromBlockActions.Actions.editBlockInstance({
          blockType: BlockTypes.Evaluation,
        })
      )
      setCreateOrEdit(BlockActionEnum.Create)
      handleSelectedOptionChange(BlockTypes.Evaluation)
      setShowAiEvaluationModal(false)
    },
    [dispatch, handleSelectedOptionChange]
  )

  useEffect(() => {
    if (!highlightBlockId) return
    const el = document.getElementById(`block-instance-${highlightBlockId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    const timer = window.setTimeout(() => setHighlightBlockId(null), 2500)
    return () => window.clearTimeout(timer)
  }, [list, highlightBlockId])

  useEffect(() => {
    if (!pendingEditBlockId) return
    const block = list.find((b) => b.id === pendingEditBlockId)
    if (block) {
      handleEdit(block)
      setPendingEditBlockId(null)
    }
  }, [list, pendingEditBlockId, handleEdit])

  return loading ? (
    <Loader overlay color />
  ) : (
    <Wrapper shadow={true}>
      <Row className="pb-4" style={{ margin: '-11px 0 0 0' }}>
        <Breadcrumb separator=">">
          <BreadcrumbItem
            href="/administration/courses"
            text={t('administration.blocks.breadcrum.init')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/edit`}
            text={t('administration.blocks.breadcrum.courseData')}
          />
          <BreadcrumbItem
            href={`/administration/courses/${courseId}/lessons`}
            text={t('administration.blocks.breadcrum.lessons')}
          />
          <BreadcrumbItem text={t('administration.blocks.breadcrum.blocks')} />
        </Breadcrumb>
      </Row>
      <Row className={`justify-content-center ${styles.lastItem}`}>
        <Col xs={12} className="pb-5">
          <div className={styles.blocksContentColumn}>
            <input
              className={`${styles.lessonName} mb-4`}
              placeholder={t('administration.lessons.lessonName')}
              value={lessonData['name']}
              onChange={(e: any) =>
                setLessonData({ ...lessonData, name: e.target.value })
              }
            />
            <div className="pt-5">
              <div className="d-flex justify-content-between align-items-end">
                <div>
                  <h1 className={`${styles.title} mb-0`}>
                    {t('administration.blocks.contentBlock')}
                  </h1>
                </div>
                <div className="d-flex align-items-center">
                  {!draggable ? (
                    <>
                      {aiEvaluationFlagOn && (
                        <CommonToolTip
                          text={
                            hasEvaluation
                              ? t(
                                  'administration.blocks.aiEvaluation.tooltips.alreadyExists'
                                )
                              : richTextCount === 0
                              ? t(
                                  'administration.blocks.aiEvaluation.tooltips.needsRichText'
                                )
                              : t(
                                  'administration.blocks.aiEvaluation.tooltips.suggestEvaluationFromTextBlocks'
                                )
                          }
                          position="bottom"
                        >
                          <div>
                            <AiEvaluationToolbarButton
                              disabled={!canUseAiEvaluation}
                              onClick={openAiEvaluationModal}
                            />
                          </div>
                        </CommonToolTip>
                      )}
                      <MoveBlocksButton
                        variant={ButtonVariant.Secondary}
                        size={ButtonSizes.Small}
                        onClick={handleSetDraggable}
                      >
                        <FontAwesomeIcon icon={faSortAlt} />
                        {t('administration.blocks.buttons.moveBlocks')}
                      </MoveBlocksButton>
                    </>
                  ) : (
                    <>
                      <CancelOrderButton
                        outline={true}
                        variant={ButtonVariant.Secondary}
                        size={ButtonSizes.Small}
                        onClick={handleCancelDraggable}
                      >
                        <FontAwesomeIcon icon={faUndo} />
                        {t('administration.blocks.buttons.cancel')}
                      </CancelOrderButton>
                      <SaveOrderButton
                        outline={true}
                        variant={ButtonVariant.Success}
                        size={ButtonSizes.Small}
                        onClick={handleSaveOrder}
                      >
                        <FontAwesomeIcon icon={faSave} />
                        {t('administration.blocks.buttons.saveOrder')}
                      </SaveOrderButton>
                    </>
                  )}
                </div>
              </div>
              <BlockList
                active={
                  selectedOption !== null
                    ? editBlock.instanceId || undefined
                    : undefined
                }
                draggable={draggable}
                blocks={listAux}
                highlightBlockId={highlightBlockId}
                onDelete={onDeleteClick}
                onEdit={(block: IBlockInstanceModel) => handleEdit(block)}
                onOrderChange={(blocks: IBlockInstanceModel[]) =>
                  setOrder(blocks)
                }
              />
              {aiEvaluationFlagOn &&
                canUseAiEvaluation &&
                !selectedOption &&
                listAux.length > 0 && (
                  <AiEvaluationSuggestion onClick={openAiEvaluationModal} />
                )}
            </div>
            <BButton
              className={styles.addBlockButton}
              disabled={
                selectedOption !== null ||
                createOrEdit === BlockActionEnum.Edit ||
                draggable
              }
              onClick={() => setShowOptions(!showOptions)}
            >
              {t(`administration.blocks.${createOrEdit}`)}
              <FontAwesomeIcon icon={faPlusCircle} />
            </BButton>
            {showOptions && (
              <div className={`${styles.blockOptionsContainer} mt-2`}>
                <div className={'d-flex flex-column align-items-center'}>
                  <CommonToolTip
                    text={t('administration.blocks.tooltips.richText')}
                    position="bottom"
                  >
                    <div>
                      <BlockTypeButton
                        text={t('administration.blocks.type.richText.name')}
                        icon={<FontAwesomeIcon icon={faFileAlt} />}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockRichText)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
                <div className={'d-flex flex-column align-items-center'}>
                  <CommonToolTip
                    text={t('administration.blocks.tooltips.multimedia')}
                    position="bottom"
                  >
                    <div>
                      <BlockTypeButton
                        text={t('administration.blocks.type.multimedia.name')}
                        icon={<FontAwesomeIcon icon={faPhotoVideo} />}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockMultimedia)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
                <div>
                  <CommonToolTip
                    text={t('administration.blocks.tooltips.survey')}
                    position="bottom"
                  >
                    <div>
                      <BlockTypeButton
                        text={t('administration.blocks.type.survey.name')}
                        icon={<FontAwesomeIcon icon={faPollH} />}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockSurvey)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
                <div>
                  <CommonToolTip
                    text={t('administration.blocks.tooltips.task')}
                    position="bottom"
                  >
                    <div>
                      <BlockTypeButton
                        text={t('administration.blocks.type.task.name')}
                        icon={<FontAwesomeIcon icon={faPaperclip} />}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockTask)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
                {videoConferenceEnable && (
                  <div>
                    <CommonToolTip
                      text={t(`administration.blocks.tooltips.videoConference`)}
                      position="bottom"
                    >
                      <div>
                        <BlockTypeButton
                          text={t(
                            'administration.blocks.type.videoConference.name'
                          )}
                          icon={<VideoConference />}
                          className={`${styles.videoConferenceBlock}`}
                          onClick={() =>
                            handleSelectedOptionChange(BlockTypes.BlockMeeting)
                          }
                        />
                      </div>
                    </CommonToolTip>
                  </div>
                )}
                {meetingsEnabled && (
                  <div data-test-id="button-add-block-sessions">
                    <CommonToolTip
                      text={t(
                        `administration.blocks.tooltips.${
                          hasSession ? 'sessionsDisabled' : 'sessions'
                        }`
                      )}
                      position="bottom"
                    >
                      <div>
                        <BlockTypeButton
                          disabled={hasSession}
                          text={t('administration.blocks.type.sessions.name')}
                          icon={<SessionsBlockIcon />}
                          onClick={() =>
                            handleSelectedOptionChange(BlockTypes.BlockSession)
                          }
                        />
                      </div>
                    </CommonToolTip>
                  </div>
                )}
                <div className={'d-flex flex-column align-items-center'}>
                  <CommonToolTip
                    text={t(
                      `administration.blocks.tooltips.${
                        hasScorm ? 'scormDisabled' : 'scorm'
                      }`
                    )}
                    position="bottom"
                  >
                    <div>
                      <BlockTypeButton
                        disabled={hasScorm}
                        text={t('administration.blocks.type.scorm.name')}
                        icon={<FontAwesomeIcon icon={faCogs} />}
                        onClick={() =>
                          handleSelectedOptionChange(BlockTypes.BlockScorm)
                        }
                      />
                    </div>
                  </CommonToolTip>
                </div>
                <div>
                  <CommonToolTip
                    text={t(
                      `administration.blocks.tooltips.${
                        hasEvaluation ? 'evaluationDisabled' : 'evaluation'
                      }`
                    )}
                    position="bottom"
                  >
                    <div>
                      <EvaluationButton
                        hasEvaluation={hasEvaluation}
                        onSelect={handleSelectedOptionChange}
                        lessonId={+lessonId}
                        totalBlocks={list.length}
                      />
                    </div>
                  </CommonToolTip>
                </div>
                {aiEvaluationFlagOn && (
                  <div>
                    <CommonToolTip
                      text={
                        hasEvaluation
                          ? t(
                              'administration.blocks.aiEvaluation.tooltips.alreadyExists'
                            )
                          : richTextCount === 0
                          ? t(
                              'administration.blocks.aiEvaluation.tooltips.needsRichText'
                            )
                          : t(
                              'administration.blocks.aiEvaluation.tooltips.suggestEvaluationFromTextBlocks'
                            )
                      }
                      position="bottom"
                    >
                      <div>
                        <AiEvaluationBlockButton
                          disabled={!canUseAiEvaluation}
                          onClick={openAiEvaluationModal}
                        />
                      </div>
                    </CommonToolTip>
                  </div>
                )}
              </div>
            )}
            {selectedOption && (
              <BlockType
                option={selectedOption}
                onCancel={handleCancel}
                editing={createOrEdit === BlockActionEnum.Edit}
              />
            )}
          </div>
        </Col>
      </Row>
      <div className={styles.actionButtons}>
        <BlocksFormFooter
          draggable={draggable}
          buttonLoading={buttonLoading}
          hasLessonName={!!lessonData['name']}
          saveDisabled={selectedOption !== null}
          onBack={() =>
            history.push(`/administration/courses/${courseId}/lessons`)
          }
          onSave={() => handleSubmit()}
        />
      </div>
      <ConfirmationDelete
        selected={blockInstance}
        show={showDeleteConfirmationModal}
        onAccept={() => {
          setShowDeleteConfirmationModal(false)
          handleDelete()
        }}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
      <AiEvaluationModal
        visible={showAiEvaluationModal}
        lessonId={+lessonId}
        lessonName={lessonData.name}
        richTextCount={richTextCount}
        onClose={() => setShowAiEvaluationModal(false)}
        onConfirm={handleAiEvaluationConfirm}
      />
    </Wrapper>
  )
}

export default Index
